/**
 * Indexable city hubs for the footer's "Deals by city" column and the home
 * page city block (web plan W3.2): cities with publicly visible stores that
 * have a hub the backend lists as indexable (D-5: ≥ 20 visible brands).
 *
 * `urlPath` is the city's hub path in the request's locale; the backend
 * returns it null while the country has no launched locale, so only cities
 * with a path are usable for navigation.
 */
import { NAV_CITIES } from '@/graphql/queries';
import { usePreloadedQuery } from '@/hooks/routePreload';
import { navCitiesVariables } from '@/seo/cityHubs';
import { linkedFromHubs, sitePath } from '@/seo/hubLinks';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import type { Locale } from '@/seo/locales';

/** A city as the nav query returns it. */
export interface NavCity {
  id: string;
  name: string;
  slug: string;
  urlPath?: string | null;
  seo?: { indexable?: boolean | null } | null;
}

export interface NavCitiesState {
  items: NavCity[];
  loading: boolean;
}

/**
 * Indexable cities for nav: those with `seo.indexable` and a site `urlPath`, of the
 * page's country (the route's locale unless one is given).
 * Only the cities the backend lists as indexable are linked from other pages
 * (master D-5).
 */
export function useNavCities(locale?: Locale): NavCitiesState {
  const routeLocale = useRouteLocale();
  // The home / nearby route module's server copy answers first (web plan
  // W4.5); the footer's copy (outside the page) loads as before.
  const { data, loading } = usePreloadedQuery<{ cities?: { cities?: NavCity[] | null } | null }>(NAV_CITIES, {
    variables: navCitiesVariables(locale ?? routeLocale),
  });
  const all = data?.cities?.cities ?? [];
  const items = all.filter((city) => linkedFromHubs(city) && sitePath(city.urlPath));
  return { items, loading };
}
